import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { paymentsTypes } from './payments.types';
import {
  handleAutoUpgrade,
  handleFetchBills,
  handleFetchPackages,
  handleFetchPaymentInfo,
  handleMakeInvoice,
  handleMakePayment,
  handleMakePaymentEng,
  handleRedirectToAutoUpgrade,
  handleSavePaymentInfo,
  handleValidateDiscountCode,
  makeInvoiceEng,
  handleFetchEngBills,
  handleCheckUserInfo,
} from './payments.helpers';
import { toast } from 'react-toastify';
import {
  setDisplayInputPaymentInfoModal,
  fetchPaymentInfoStart,
  setBills,
  setPackages,
  setDiscountCode,
  setInvoiceId,
  setIsAutoUpgradeVisible,
  setIsSelectBankVisible,
  setPaymentInfo,
  setSelectedBank,
  setSelectedUpgradeMethod,
  setSelectedPackageType,
  loadingPackages,
  loadingBills,
  loadingPaymentInfo,
  setPackageTypes,
  setUseWhatsappPackages,
  makeInvoiceLoading,
  setShowDiscountError,
} from './payments.actions';
import {
  createBillsObject,
  createPaymentInfoObject,
  createPackagesObject,
  createPackageTypesObject,
  createUseWhatsappPackages,
  getApiReadyDataForEngInvoice,
  createEngBillsObject,
  getInfoSpecialErrorMessage,
  getPayloadDataForUserInfoApi,
  getCheckOfficialCustomError,
} from './payments.adapter';
import { handleValidateSmsVerificationCode } from './../user/user.helpers';
import {
  setVerificationCodeTimerStatus,
  setIsMobileValidated,
} from '../app/app.actions';
import { getNotifMsg, handleErrors } from '../../utils';

const mapState = ({ appData }) => ({
  locale: appData.locale,
});

export function* fetchBills() {
  const { locale } = yield select(mapState);

  try {
    yield put(loadingBills());
    const bills = yield handleFetchBills();
    yield put(setBills(createBillsObject(bills)));
  } catch (error) {
    handleErrors(error, locale);
  }
}

function* fetchEngBillsStart() {
  const { locale } = yield select(mapState);

  try {
    yield put(loadingBills());
    const response = yield handleFetchEngBills();

    if (response.success) {
      yield put(setBills(createEngBillsObject(response.data)));
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

function* onFetchEngBillsStart() {
  yield takeLatest(paymentsTypes.FETCH_ENG_BILLS_START, fetchEngBillsStart);
}

export function* fetchPackages() {
  const { locale } = yield select(mapState);
  try {
    yield put(loadingPackages());
    const packages = yield handleFetchPackages();
    let packagesObject = createPackagesObject(packages);
    yield put(setPackages(packagesObject));
    yield put(setPackageTypes(createPackageTypesObject(packages.all)));
    yield put(setUseWhatsappPackages(createUseWhatsappPackages(packages.all)));
    yield put(setSelectedPackageType(packagesObject['premium'].packageType));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onFetchBillsStart() {
  yield takeLatest(paymentsTypes.FETCH_BILLS_START, fetchBills);
}

export function* onFetchPackagesStart() {
  yield takeLatest(paymentsTypes.FETCH_PACKAGES_START, fetchPackages);
}

export function* validateDiscountCode({ payload }) {
  const { locale } = yield select(mapState);
  const { code, selectedPackageType } = payload;

  try {
    const returnedData = yield handleValidateDiscountCode({
      code,
      packType: selectedPackageType,
    });

    if (returnedData.status === -1) {
      const discountError = getNotifMsg('ENTERED_CODE_IS_WRONG', locale);
      yield put(setShowDiscountError({ discountError, show: true }));
      return;
    }

    // error when offer-code is just for package upgrade purchases but user is not upgrading
    if (
      returnedData.status === -3 &&
      returnedData.message === 'Just upgrade package'
    ) {
      const discountError = getNotifMsg(
        'DISCOUNT_CODE_ONLY_FOR_UPGRADE_PACKAGE',
        locale
      );
      yield put(setShowDiscountError({ discountError, show: true }));
      return;
    }

    // error when offer-code is not for this account owner
    if (
      returnedData.status === -3 &&
      returnedData.message === 'access denied'
    ) {
      const discountError = getNotifMsg('DISCOUNT_CODE_NOT_FOR_YOU', locale);
      yield put(setShowDiscountError({ discountError, show: true }));
      return;
    }

    // error when offer-code is used twice, while it should only be used in first purchase
    if (
      returnedData.status === -3 &&
      returnedData.message === 'Just first time'
    ) {
      const discountError = getNotifMsg(
        'DISCOUNT_JUST_FOR_FIRST_FACTOR',
        locale
      );
      yield put(setShowDiscountError({ discountError, show: true }));
      return;
    } else if (returnedData.offPrice) {
      yield put(
        setDiscountCode({
          discountInfo: returnedData,
          discountCode: returnedData.code,
        })
      );
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onValidateDiscountCodeStart() {
  yield takeLatest(
    paymentsTypes.VALIDATE_DISCOUNT_CODE_START,
    validateDiscountCode
  );
}

export function* makeInvoice({ payload }) {
  const { handleToggle } = payload;
  const { locale } = yield select(mapState);

  try {
    yield put(makeInvoiceLoading(true));
    const returnedData = yield handleMakeInvoice(payload);
    yield put(makeInvoiceLoading(false));
    yield put(setInvoiceId(returnedData.id));
    yield put(fetchPaymentInfoStart());
    yield put(setDisplayInputPaymentInfoModal(true));
    handleToggle();
  } catch (error) {
    if (error.response && error.response.status === 400) {
      if (
        error.response?.data?.message ===
        'package is not avaliable.please contact support.'
      ) {
        toast.error(
          'برای خرید این پکیج لطفا با پشتیبانی رایچت در چت آنلاین  ارتباط بگیرید .'
        );
        return;
      }
      toast.error(getNotifMsg('CANT_DOWNGRADE_PACKAGE', locale));
      yield put(makeInvoiceLoading(false));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* onMakeInvoiceStart() {
  yield takeLatest(paymentsTypes.MAKE_INVOICE_START, makeInvoice);
}

export function* savePaymentInfo({ payload }) {
  const {
    selectedForm,
    values,
    handleToggle,
    setCheckBeforePurchaseStatus,
    setLoadingSubmit,
    redirectToDargahAfterSuccess,
  } = payload;

  const { locale } = yield select(mapState);
  try {
    const payloadData = getPayloadDataForUserInfoApi({
      ...values,
      selectedForm,
    });

    const response = yield handleSavePaymentInfo(payloadData);

    // const response = { success: true };

    if (response.success) {
      toast.success(getNotifMsg('SUCCESSFUL_UPDATING_INFO', locale));
      yield handleToggle();
      // yield put(fetchPaymentInfo());

      const returnedData = yield handleFetchPaymentInfo();
      yield put(setPaymentInfo(createPaymentInfoObject(returnedData.data)));

      yield setCheckBeforePurchaseStatus(false);
      if (redirectToDargahAfterSuccess) {
        yield put(setIsAutoUpgradeVisible(true));
      }
      yield put(setSelectedUpgradeMethod('manual'));
    }
  } catch (error) {
    const saveInfospecialError = getInfoSpecialErrorMessage(error);

    if (saveInfospecialError) {
      toast.error(saveInfospecialError);
      return;
    }

    handleErrors(error, locale);
  } finally {
    setLoadingSubmit(false);
  }
}
export function* onSavePaymentInfoStart() {
  yield takeLatest(paymentsTypes.SAVE_PAYMENT_INFO_START, savePaymentInfo);
}

export function* makePaymentWithoutOfficialBill({ payload }) {
  const { locale } = yield select(mapState);

  try {
    yield put(makeInvoiceLoading(true));
    const returnedData = yield handleMakeInvoice(payload);
    yield put(makeInvoiceLoading(false));

    if (!returnedData.success) {
      toast.error(getNotifMsg('PROBLEM_WHILE_MAKING_INVOICE', locale));
      return;
    }

    if (payload.isPackageUpgrade) {
      yield put(setInvoiceId(returnedData.id));
      yield put(setIsAutoUpgradeVisible(true));
      yield put(setSelectedUpgradeMethod('manual'));
    } else {
      handleMakePayment(returnedData.id);
    }
  } catch (error) {
    if (error.response && error.response.status === 400) {
      if (
        error.response?.data?.message ===
        'package is not avaliable.please contact support.'
      ) {
        toast.error(
          'برای خرید این پکیج لطفا با پشتیبانی رایچت در چت آنلاین  ارتباط بگیرید .'
        );
        return;
      }

      toast.error(getNotifMsg('CANT_DOWNGRADE_PACKAGE', locale));
      yield put(makeInvoiceLoading(false));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* onMakePaymentWithoutOfficialBillStart() {
  yield takeLatest(
    paymentsTypes.MAKE_PAYMENT_WITHOUT_OFFICIAL_BILL_START,
    makePaymentWithoutOfficialBill
  );
}

export function* makePaymentWithoutOfficialBillEng({ payload }) {
  const { locale } = yield select(mapState);

  try {
    // package upgrade = false --> conversation change operation
    const apiData = getApiReadyDataForEngInvoice(payload);
    const returnedData = yield makeInvoiceEng(apiData);

    if (!returnedData.success) {
      toast.error(getNotifMsg('PROBLEM_WHILE_MAKING_INVOICE', locale));
      return;
    }

    if (payload.isPackageUpgrade) {
      yield put(setInvoiceId(returnedData.data.id));
      yield put(setIsAutoUpgradeVisible(true));
      yield put(setSelectedUpgradeMethod('manual'));
    } else {
      handleMakePaymentEng(returnedData.data.id);
    }
  } catch (error) {
    if (error.response && error.response.status === 400) {
      if (
        error.response?.data?.message ===
        'package is not avaliable.please contact support.'
      ) {
        toast.error(
          'برای خرید این پکیج لطفا با پشتیبانی رایچت در چت آنلاین  ارتباط بگیرید .'
        );
        return;
      }
      toast.error(getNotifMsg('CANT_DOWNGRADE_PACKAGE', locale));
      yield put(makeInvoiceLoading(false));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* onMakePaymentWithoutOfficialBillStartEng() {
  yield takeLatest(
    paymentsTypes.MAKE_INVOICE_WITHOUT_OFFICIAL_BILL_ENG,
    makePaymentWithoutOfficialBillEng
  );
}

// NOTE: looks like this function is not doing anything. consider checking it
export function* fetchPaymentReport({ payload }) {
  const { locale } = yield select(mapState);
  try {
    // const returnedData = yield handleFetchPaymentReport(payload);
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onFetchPaymentReportStart() {
  yield takeLatest(
    paymentsTypes.FETCH_PAYMENT_REPORT_START,
    fetchPaymentReport
  );
}

export function* fetchPaymentInfo() {
  const { locale } = yield select(mapState);
  try {
    yield put(loadingPaymentInfo());
    const returnedData = yield handleFetchPaymentInfo();
    if (!returnedData.success) {
      yield put(setPaymentInfo({}));
      return;
    }
    yield put(setPaymentInfo(createPaymentInfoObject(returnedData.data)));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onFetchPaymentInfoStart() {
  yield takeLatest(paymentsTypes.FETCH_PAYMENT_INFO_START, fetchPaymentInfo);
}

export function* validateSmsAndSaveUserInfo({ payload }) {
  const {
    verificationValues,
    userInfo,
    handleToggle,
    setLoadingSubmit,
    doAfterSuccessfullUpdate,
  } = payload;

  const { locale } = yield select(mapState);

  try {
    const validationResponse = yield handleValidateSmsVerificationCode(
      verificationValues
    );

    if (!validationResponse?.success) {
      toast.error(getNotifMsg('ENTERED_CODE_WRONG_TRY_AGAIN', locale), {
        position: 'bottom-center',
      });
      return;
    }
    yield put(setVerificationCodeTimerStatus(false));
    yield put(setIsMobileValidated(true));
    verificationValues.toggle();

    const payloadData = getPayloadDataForUserInfoApi({
      ...userInfo.values,
      selectedForm: userInfo.selectedForm,
    });

    const returnedData = yield handleSavePaymentInfo(payloadData);

    if (returnedData.success) {
      yield put(setPaymentInfo({ ...userInfo.values }));
      toast.success(getNotifMsg('SUCCESSFUL_UPDATING_INFO', locale));
      doAfterSuccessfullUpdate({
        ...userInfo.values,
        selectedForm: userInfo.selectedForm,
      });
      // close form modal
      handleToggle();
      return;
    }
  } catch (error) {
    if (error.response?.status === 406) {
      toast.error(getNotifMsg('ENTERED_CODE_WRONG_TRY_AGAIN', locale));
      return;
    }
    handleErrors(error, locale);
  } finally {
    setLoadingSubmit(false);
  }
}

export function* onValidateSmsAndSaveUserInfo() {
  yield takeLatest(
    paymentsTypes.VALIDATE_SMS_AND_SAVE_USER_INFO,
    validateSmsAndSaveUserInfo
  );
}

export function* updateInCompleteUserInfoByModal({ payload }) {
  const { locale } = yield select(mapState);

  const { values, selectedForm, setLoadingSubmit, doAfterSuccessfullUpdate } =
    payload;

  try {
    const payloadData = getPayloadDataForUserInfoApi({
      ...values,
      selectedForm,
    });

    const returnedData = yield handleSavePaymentInfo(payloadData);

    if (returnedData.success) {
      toast.success(getNotifMsg('SUCCESSFUL_UPDATING_INFO', locale));

      // TODO: ideally payment-info update should get done by the returned data not by a new request
      // fetch user info
      const freshPaymentInfoData = yield handleFetchPaymentInfo();

      yield put(
        setPaymentInfo(createPaymentInfoObject(freshPaymentInfoData.data))
      );
      doAfterSuccessfullUpdate(freshPaymentInfoData.data);
    }
  } catch (error) {
    const saveInfospecialError = getInfoSpecialErrorMessage(error);

    if (saveInfospecialError) {
      toast.error(saveInfospecialError);
      return;
    }

    handleErrors(error, locale);
  } finally {
    setLoadingSubmit(false, selectedForm);
  }
}

export function* onUpdateInCompleteUserInfoByModal() {
  yield takeLatest(
    paymentsTypes.UPDATE_IN_COMPLETE_USER_INFO_BY_MODAL,
    updateInCompleteUserInfoByModal
  );
}

function* checkUserInformation({ payload }) {
  const { data, setData, setLoading } = payload;

  const { locale } = yield select(mapState);
  const apiPayload = getPayloadDataForUserInfoApi(data);

  try {
    const response = yield handleCheckUserInfo(apiPayload);

    // when national ID and mobile number are not related
    if (!response.data.matched) {
      toast.warning('کدملی با شماره تلفن همخوانی ندارد');
      return;
    }

    setData(createPaymentInfoObject(response.data));
  } catch (error) {
    const errorData = error?.response?.data;
    const errorsArray = errorData?.errors;
    const firstError = errorsArray?.[0];

    const providerError = errorData?.data?.message === 'provider error';

    if (providerError) {
      toast.warning(
        'مشکلی در استعلام اطلاعات شما پیش آمده است. لطفا فرم مورد نظر را تکمیل و گزینه ی ذخیره را بزنید'
      );

      setData({ ...payload.data, notCompare: true });
      return;
    }

    const customError = getCheckOfficialCustomError(firstError);

    if (customError) {
      toast.warning(customError);
      return;
    }

    handleErrors(error, locale);
  } finally {
    setLoading(false, data.selectedForm);
  }
}

function* onCheckUserInformation() {
  yield takeLatest(paymentsTypes.CHECK_USER_INFORMATION, checkUserInformation);
}

export function* displaySelectBankModal({ payload }) {
  const { handleToggleModal } = payload;
  yield put(setIsSelectBankVisible(true));
  yield put(setSelectedBank(''));
  if (handleToggleModal) {
    handleToggleModal();
  }
}

export function* onDispalySelectBankModalStart() {
  yield takeLatest(
    paymentsTypes.DISPLAY_SELECT_BANK_MODAL_START,
    displaySelectBankModal
  );
}

export function* redirectToAutoUpgrade({ payload }) {
  const { locale } = yield select(mapState);
  try {
    const returnedData = yield handleAutoUpgrade(payload);
    handleRedirectToAutoUpgrade(returnedData.url);
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onRedirectToAutoUpgradeStart() {
  yield takeLatest(
    paymentsTypes.REDIRECT_TO_AUTO_UPGRADE_START,
    redirectToAutoUpgrade
  );
}

export default function* paymentsSagas() {
  yield all([
    call(onFetchBillsStart),
    call(onFetchEngBillsStart),
    call(onFetchPackagesStart),
    call(onValidateDiscountCodeStart),
    call(onMakeInvoiceStart),
    call(onSavePaymentInfoStart),
    call(onMakePaymentWithoutOfficialBillStart),
    call(onFetchPaymentReportStart),
    call(onFetchPaymentInfoStart),
    call(onUpdateInCompleteUserInfoByModal),
    call(onDispalySelectBankModalStart),
    call(onRedirectToAutoUpgradeStart),
    call(onValidateSmsAndSaveUserInfo),
    call(onMakePaymentWithoutOfficialBillStartEng),
    call(onFetchEngBillsStart),
    call(onCheckUserInformation),
  ]);
}
